import { ArtifactsPage } from '../artifacts/ArtifactsPage'
import type { ArtifactRow } from '../artifacts/everywhere'
import { BackupsPage } from '../backup/BackupsPage'
import type { SharedArtifact } from '../transport/relay'
import { ChromeTabsPage } from './ChromeTabsPage'
import type { MorePage } from './MorePanel'
import { BookmarksPage } from './BookmarksPage'
import { WhiteboardsPage } from './WhiteboardsPage'
import { AllPagesPage, pageRooms } from './AllPagesPage'
import type { AgentRow, Bookmark, ProjectRow } from '../transport/api'

type OpenAgent = (a: { session: string; hash: string; device: string }) => void

/**
 * WHERE MORE'S PAGES ARE SHOWN — owner, 2026-10-22: 在寬螢幕的狀態下, 點擊 Artifact /
 * Chrome tabs / Backups 按鈕會在右側主顯示欄位 (session) 裡開啟並且列表所有的 … 如果是在
 * 窄螢幕的狀態, 則是使用蓋板的方式來顯示.
 *
 * One frame, #moreStage, placed the way the artifact viewer is (shell.css): from the rail's
 * docked width to the right edge, so on a wide screen it covers the stage and leaves the
 * rail — and its More buttons — beside it; on a phone the rail docks nowhere, the width is
 * 0, and it covers the screen. Each page inside draws its own header (StagePage), because
 * only the page knows whether Back means "to the list" or "out of More".
 *
 * An artifact opened from its list is the viewer, over this frame; its Back closes it and
 * the list is here underneath.
 */
export function MoreStage({
  page,
  onClose,
  railOpen,
  onToggleRail,
  onOpenArtifact,
  onOpenShared,
  onOpenAgent,
  onGoToAgent,
  agents = [],
  projects = [],
  onGoAgentBookmark = () => {},
  onGoRoomBookmark = () => {},
  onOpenBoard = () => {},
  onOpenPage = () => {},
}: {
  page: MorePage
  /** Back from a list: the stage shows what it showed before. */
  onClose: () => void
  railOpen: boolean
  onToggleRail: () => void
  onOpenArtifact: (a: ArtifactRow) => void
  /** A friend's artifact — its own door into the viewer (owner, 2026-10-05). */
  onOpenShared: (a: SharedArtifact) => void
  /** A restored backup's "Open it": the new agent on the machine it landed on. */
  onOpenAgent?: OpenAgent
  /** An agent that already exists, opened where it is — a Chrome tab's agent. */
  onGoToAgent?: OpenAgent
  /** Bookmarks and Whiteboards gather from these. */
  agents?: AgentRow[]
  projects?: ProjectRow[]
  /** A bookmark's message, in its agent's conversation or its project. */
  onGoAgentBookmark?: (agent: AgentRow, b: Bookmark) => void
  onGoRoomBookmark?: (room: ProjectRow, b: Bookmark) => void
  /** A whiteboard, in its project. */
  onOpenBoard?: (room: ProjectRow, board: string) => void
  /** A page, in its project. */
  onOpenPage?: (room: ProjectRow, page: string) => void
}) {
  const chrome = { onBack: onClose, railOpen, onToggleRail }
  return (
    <div id="moreStage">
      {/* Keyed by the page: another part is another page, its open item and scroll its own. */}
      {page === 'artifacts' ? (
        <ArtifactsPage key={page} {...chrome} onOpenArtifact={onOpenArtifact} onOpenShared={onOpenShared} />
      ) : page === 'tabs' ? (
        <ChromeTabsPage key={page} {...chrome} {...(onGoToAgent ? { onGoToAgent } : {})} />
      ) : page === 'bookmarks' ? (
        <BookmarksPage
          key={page}
          {...chrome}
          agents={agents}
          rooms={projects.filter((p) => !p.group_id)}
          onGoAgent={onGoAgentBookmark}
          onGoRoom={onGoRoomBookmark}
        />
      ) : page === 'boards' ? (
        <WhiteboardsPage key={page} {...chrome} rooms={projects} onOpenBoard={onOpenBoard} />
      ) : page === 'pages' ? (
        <AllPagesPage key={page} {...chrome} rooms={pageRooms(projects)} onOpenPage={onOpenPage} />
      ) : (
        <BackupsPage key={page} {...chrome} {...(onOpenAgent ? { onOpenAgent } : {})} />
      )}
    </div>
  )
}
